/* eslint-disable indent */
import React, { useEffect } from 'react';
import { FormData, FormErrors, IUpsertClientDialog } from './interfaces';
import {
  AppBar,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SelectField } from '../../../../components/forms/SelectField';
import { isEmpty } from '../../../../helpers/formHelpers';
import {
  useGetSectorList,
  useGetSubSectorList,
} from '../../../../reactQuery/sectors';
import { LoadingLayover } from '../../../../components/LoadingLayover';
import { Sector } from '../../../../api/sectorsApi/interfaces';
import { NumberField } from '../../../../components/forms/NumberField';
import { enqueueSnackbar } from 'notistack';
import { useGetGroupList } from '../../../../reactQuery/groups';
import { useGetIslandsList } from '../../../../reactQuery/islands';
import { Status } from '../../../../api/clientsApi/interfaces';
import { useGetAgentsList } from '../../../../reactQuery/Agents/agents';
import {
  useCreateClientMutation,
  useGetAreasList,
  useUpdateClientMutation,
} from '../../../../reactQuery/clients';
import { useNavigate } from 'react-router';
import { MutateOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useGetMealPlansList } from '../../../../reactQuery/mealPlans';
import { useGetproductsList } from '../../../../reactQuery/products';
import useFormState from '../../../../hooks/useFormState';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import { TagsField } from '../../../../components/forms/TagsField';
import { useGetContactsList } from '../../../../reactQuery/contacts';

export const UpsertClientDialog: React.FC<IUpsertClientDialog> = ({
  client,
  show,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: createClient, isLoading: isCreatingClient } =
    useCreateClientMutation();
  const { mutate: updateClient, isLoading: isUpdatingClient } =
    useUpdateClientMutation();

  const theme = useTheme<CustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isEditMode = !!client;

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges<FormData>(formData);

  const {
    data: sectorList,
    isLoading: loadingSectorList,
    error: sectorListError,
  } = useGetSectorList(show);

  const {
    data: groupList,
    isLoading: loadingGroupList,
    error: groupListError,
  } = useGetGroupList(show);

  const {
    data: islandList,
    isLoading: loadingIslandList,
    error: islandListError,
  } = useGetIslandsList(show);

  const {
    data: areasList,
    isLoading: loadingAreasList,
    error: areasListError,
  } = useGetAreasList(show);

  const {
    data: agentsList,
    isLoading: loadingAgentsList,
    error: agentsListError,
  } = useGetAgentsList(show);

  const {
    data: mealPlanList,
    isLoading: loadingMealPlanList,
    error: mealPlanListError,
  } = useGetMealPlansList(show);

  const {
    data: productsList,
    isLoading: loadingProductsList,
    error: ProductsListError,
  } = useGetproductsList(show);

  const {
    data: contactList,
    isLoading: loadingContactList,
    error: ContactListError,
  } = useGetContactsList(show);

  // TODO: What to do when the sub sectors fails to load?
  const { data: subSectorList, error: subSectorListError } =
    useGetSubSectorList(formData.sectorId);

  const isLoadingListOptions =
    loadingSectorList ||
    loadingGroupList ||
    loadingIslandList ||
    loadingAreasList ||
    loadingAgentsList ||
    loadingMealPlanList ||
    loadingContactList ||
    loadingProductsList;
  const selectedSector = ((): Sector | null => {
    if (!formData.sectorId || !sectorList) {
      return null;
    }

    return sectorList.find((sector) => sector.id === formData.sectorId) || null;
  })();
  const selectedSubSector = ((): Sector | null => {
    if (!formData.subSectorId || !subSectorList) {
      return null;
    }

    return (
      subSectorList.find((sector) => sector.id === formData.subSectorId) || null
    );
  })();
  const hasSubSections = subSectorList && subSectorList.length > 0;
  const hasRooms = selectedSector?.hasRooms || selectedSubSector?.hasRooms;
  const hasMealPlan =
    selectedSector?.hasMealPlan || selectedSubSector?.hasMealPlan;

  const canClientHaveContractedProducts = (): boolean => {
    return formData.status === Status.ACTIVE;
  };

  const canClientHavePotentialProducts = (): boolean => {
    return (
      formData.status === Status.ACTIVE || formData.status === Status.POTENTIAL
    );
  };

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.name || isEmpty(formData.name)) {
      errors.name = t('errors.required');
    }

    if (formData.sectorId === undefined) {
      errors.sectorId = t('errors.pleaseSelectAnOption');
    } else if (hasSubSections && formData.subSectorId === undefined) {
      errors.subSectorId = t('errors.pleaseSelectAnOption');
    }

    if (hasMealPlan && !formData.mealPlan?.length) {
      errors.mealPlan = t('errors.pleaseSelectAnOption');
    }

    if (hasRooms && formData.numberOfRooms === undefined) {
      errors.numberOfRooms = t('errors.required');
    }

    if (formData.islandId === undefined) {
      errors.islandId = t('errors.pleaseSelectAnOption');
    }

    if (formData.status === undefined) {
      errors.status = t('errors.pleaseSelectAnOption');
    }

    setFormErrors(errors);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!Object.keys(formErrors).length) {
      const clientData = {
        name: formData.name as string,
        status: formData.status as Status,
        sectorId: (formData.subSectorId || formData.sectorId) as number,
        islandId: formData.islandId as number,
        groupId: formData.groupTags?.length ? formData.groupTags[0] : undefined,
        areaId: formData.areasTags?.length ? formData.areasTags[0] : undefined,
        numberOfRooms: hasRooms
          ? (formData.numberOfRooms as number)
          : undefined,
        mealPlan: hasMealPlan ? formData.mealPlan : undefined,
        contractedProducts: canClientHaveContractedProducts()
          ? formData.contractedProductsTags
          : undefined,
        potentialProducts: canClientHavePotentialProducts()
          ? formData.potentialProductsTags
          : undefined,
        responsibleAgents: formData.responsibleAgents,
        contacts: formData.contacts,
        contactInterval:
          formData.contactInterval === 0 ? undefined : formData.contactInterval,
      };

      const mutateOptions:
        | MutateOptions<number, AxiosError, unknown>
        | undefined = {
        onSuccess: (clientId) => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateClient.success')
              : t('forms:createClient.success'),
            {
              variant: 'success',
            }
          );

          navigate(`/clients/${clientId}`);
          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateClient.failed')
              : t('forms:createClient.failed'),
            {
              variant: 'error',
            }
          );
        },
      };

      if (isEditMode) {
        updateClient(
          {
            clientId: client.id,
            clientData,
          },
          mutateOptions
        );
      } else {
        createClient(clientData, mutateOptions);
      }
    }
  };

  useEffect(() => {
    const error =
      sectorListError ||
      groupListError ||
      islandListError ||
      areasListError ||
      agentsListError ||
      mealPlanListError ||
      ProductsListError ||
      ContactListError;

    if (error) {
      enqueueSnackbar(t('errors.somethingWentWrong'), {
        variant: 'error',
      });

      onClose();
    }
  }, [sectorListError, groupListError, islandListError, agentsListError]);

  useEffect(() => {
    setFormErrors({});

    if (show && client) {
      setFormData({
        name: client.name,
        groupTags: client.group ? [client.group.id] : [],
        islandId: client.island.id,
        status: client.status,
        mealPlan: client.mealPlan.map((mealPlan) => mealPlan.id),
        areasTags: client.area ? [client.area.id] : undefined,
        sectorId: client.sector.parentId || client.sector.id,
        subSectorId: client.sector.parentId ? client.sector.id : undefined,
        numberOfRooms: client.numberOfRooms,
        potentialProductsTags: client.potentialProducts?.map(
          (product) => product.id
        ),
        contractedProductsTags: client.contractedProducts?.map(
          (product) => product.id
        ),
        responsibleAgents: client.responsibleAgents.map((agent) => agent.id),
        contacts: client.contacts.map((contact) => contact.id),
        contactInterval: client.contactInterval,
      });
    } else {
      setFormData({});
    }
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {isEditMode
              ? t('forms:updateClient.title')
              : t('forms:createClient.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isCreatingClient || isUpdatingClient}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
        }}
      >
        {isLoadingListOptions ? (
          <LoadingLayover height="200px" />
        ) : (
          <Stack
            gap="15px"
            component="form"
            id="createClientForm"
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          >
            <TextField
              label={t('entities:client.name')}
              variant="outlined"
              size="small"
              value={formData.name}
              placeholder={client?.name}
              onChange={(e) => setFormFieldData('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />

            <TagsField
              existingTags={groupList}
              label={t('entities:client.group')}
              value={formData.groupTags || []}
              onChange={(value) => {
                setFormFieldData('groupTags', value);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
              maxTags={1}
              createTags
            />

            <SelectField
              name="island"
              label={t('entities:client.island')}
              value={formData.islandId ? String(formData.islandId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('islandId', Number(event.target.value))
              }
              error={!!formErrors.islandId}
              errorMessage={formErrors.islandId}
              required
            >
              {islandList?.map((island) => (
                <MenuItem key={island.id} value={island.id}>
                  {island.name}
                </MenuItem>
              ))}
            </SelectField>

            <TagsField
              existingTags={areasList}
              label={t('entities:client.area')}
              value={formData.areasTags || []}
              onChange={(value) => {
                setFormFieldData('areasTags', value);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
              maxTags={1}
              createTags
            />

            <SelectField
              name="sector"
              label={t('entities:client.sector')}
              value={formData.sectorId ? String(formData.sectorId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('sectorId', Number(event.target.value))
              }
              error={!!formErrors.sectorId}
              errorMessage={formErrors.sectorId}
              required
            >
              {sectorList?.map((sector) => (
                <MenuItem key={sector.id} value={sector.id}>
                  {sector.name}
                </MenuItem>
              ))}
            </SelectField>

            {hasSubSections && (
              <SelectField
                name="sub-sector"
                label={t('entities:sector.parent')}
                value={formData.subSectorId ? String(formData.subSectorId) : ''}
                onChange={(event: SelectChangeEvent) =>
                  setFormFieldData('subSectorId', Number(event.target.value))
                }
                error={!!formErrors.subSectorId}
                errorMessage={formErrors.subSectorId}
                required
              >
                {subSectorList?.map((sector) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>
                ))}
              </SelectField>
            )}

            {hasMealPlan && mealPlanList && (
              <TagsField
                existingTags={mealPlanList}
                label={t('entities:client.mealPlan')}
                error={formErrors.mealPlan}
                value={formData.mealPlan || []}
                onChange={(value) => {
                  setFormFieldData('mealPlan', value as number[]);
                }}
                sx={{
                  maxWidth: fullScreen ? undefined : '400px',
                }}
              />
            )}

            {hasRooms && (
              <NumberField
                label={t('entities:client.numberOfRooms')}
                value={formData.numberOfRooms || ''}
                placeholder={
                  client?.numberOfRooms
                    ? String(client.numberOfRooms)
                    : undefined
                }
                error={!!formErrors.numberOfRooms}
                helperText={formErrors.numberOfRooms}
                required
                onChange={(e) => {
                  const value = e.target.value;

                  setFormFieldData(
                    'numberOfRooms',
                    value === '' ? undefined : Number(value)
                  );
                }}
              />
            )}

            <SelectField
              name="status"
              label={t('entities:client.status')}
              value={formData.status ? String(formData.status) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('status', String(event.target.value))
              }
              error={!!formErrors.status}
              errorMessage={formErrors.status}
              required
            >
              {Object.values(Status).map((statusKey) => (
                <MenuItem key={statusKey} value={statusKey}>
                  {t(`entities:client.enums.status.${statusKey}`)}
                </MenuItem>
              ))}
            </SelectField>

            {canClientHaveContractedProducts() && productsList && (
              <TagsField
                existingTags={productsList}
                label={t('entities:client.contractedProducts')}
                value={formData.contractedProductsTags || []}
                onChange={(value) => {
                  setFormFieldData('contractedProductsTags', value);
                }}
                sx={{
                  maxWidth: fullScreen ? undefined : '400px',
                }}
                createTags
              />
            )}

            {canClientHavePotentialProducts() && productsList && (
              <TagsField
                existingTags={productsList}
                label={t('entities:client.potentialProducts')}
                value={formData.potentialProductsTags || []}
                onChange={(value) => {
                  setFormFieldData('potentialProductsTags', value);
                }}
                sx={{
                  maxWidth: fullScreen ? undefined : '400px',
                }}
                createTags
              />
            )}

            <TagsField
              existingTags={contactList?.map((contact) => {
                return {
                  id: contact.id,
                  name: `${contact.firstName} ${contact.lastName}`,
                };
              })}
              label={t('entities:client.contacts')}
              value={formData.contacts || []}
              onChange={(value) => {
                setFormFieldData('contacts', value as number[]);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
            />
            <SelectField
              name="contactInterval"
              label={t('entities:client.contactInterval')}
              value={
                formData.contactInterval
                  ? String(formData.contactInterval)
                  : '0'
              }
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('contactInterval', Number(event.target.value))
              }
              required
            >
              <MenuItem key="none" value="0">
                {t('forms:createClient.fields.contactInterval.noInterval')}
              </MenuItem>
              {Array.from({ length: 12 }, (_, index) => (
                <MenuItem key={index + 1} value={(index + 1) * 30}>
                  {t('words.month', { count: index + 1 })}
                </MenuItem>
              ))}
            </SelectField>

            <TagsField
              existingTags={agentsList?.map((agent) => {
                return {
                  id: agent.id,
                  name: `${agent.firstName} ${agent.lastName}`,
                };
              })}
              label={t('entities:client.responsibleAgents')}
              value={formData.responsibleAgents || []}
              onChange={(value) => {
                setFormFieldData('responsibleAgents', value as number[]);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isCreatingClient || isUpdatingClient}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          form="createClientForm"
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={
            isLoadingListOptions || isCreatingClient || isUpdatingClient
          }
        >
          {isEditMode ? t('buttons.update') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
